import { Component } from '@angular/core';
import { Subject, filter, firstValueFrom, take, takeUntil, timeout } from 'rxjs';
import { CustomErrorHandler } from '../../services/error-handler.service';
import { TokenizationService } from '../../services/tokenization.service';
import { AuthResponseStatus as AuthStatus, ErrorType, TokenizationFlowStep } from '../../enums/enums.enum';
import { Globals } from '../../globals';
import { AnalyticsService } from '../../services/analytics.service';
import { ErrorModel } from '../../models/error-model';
import { TokenizationFlowService } from '../../services/tokenization-flow.service';
import { PreAuthenticationModel } from '../../models/pre-authentication-model';
import { AuthenticationModel } from '../../models/authentication-model';
import { PostAuthenticationModel } from '../../models/post-authentication-model';
import { EnrollModel } from '../../models/enroll-model';

@Component({
  selector: 'app-component',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss']
})
export class AppComponent {
  openIframeConsumerAuthentication = false;

  private unsubscriber: Subject<void> = new Subject();
  
  loading = false;

  private dataCollectionOrigin: string | null = null;

  readonly flowStep$ = this.tokenizationFlow.flowStep$;
  readonly TokenizationFlowStep = TokenizationFlowStep;

  readonly simulationEnabled = false;

  callbackUrl = Globals.originUrl;

  constructor(
    private tokenization: TokenizationService,
    private errorHandler: CustomErrorHandler,
    private analytics: AnalyticsService,
    private tokenizationFlow: TokenizationFlowService
  ) {}

  simulateReset(): void {
    this.tokenizationFlow.setFlowStep(TokenizationFlowStep.IDLE);
  }

  simulatePrevious(): void {
    const current = this.tokenizationFlow.getFlowStep();
    const prev = Math.max(TokenizationFlowStep.IDLE, current - 1);
    this.tokenizationFlow.setFlowStep(prev);
  }

  simulateNext(): void {
    const current = this.tokenizationFlow.getFlowStep();
    const next = Math.min(TokenizationFlowStep.SUCCESS, current + 1);
    this.tokenizationFlow.setFlowStep(next);
  }

  ngOnInit() {
    this.addMessageListener();
  }
  
  /* 
    Escucha eventos tipo message provenientes de iframes.
    Lo usamos para recibir el status de la autenticación post-challenge cuando
    se realice una redirección a esta misma web con parámetro callback=true, y 
    proseguir con el enrolamiento
  */
  private addMessageListener() {
    window.addEventListener("message", (event) => {
      const allowedOrigins = new Set([
        this.callbackUrl,
        ...(this.dataCollectionOrigin ? [this.dataCollectionOrigin] : []),
        ...Globals.dataCollectionMessageOrigins
      ]);

      if (!allowedOrigins.has(event.origin)) return;

      if (
        (this.dataCollectionOrigin && event.origin === this.dataCollectionOrigin) ||
        Globals.dataCollectionMessageOrigins.has(event.origin)
      ) {
        this.handleDataCollectionMessage(event);
        return;
      }

      if (event.origin === this.callbackUrl) {
        this.handleAuthCallbackMessage(event);
        return;
      }
    });
  }

  private handleDataCollectionMessage(event: MessageEvent): void {
    let data = event.data;
    if (typeof data === "string") {
      try {
        data = JSON.parse(data);
      } catch (error) {
        this.errorHandler.reportError(error, ErrorType.UNEXPECTED_DATA_COLLECTION_RESPONSE);
        return;
      }
    }

    if (
      data?.MessageType === 'profile.completed' &&
      data?.Status === true
    ) {
      this.tokenization.updateDataCollectionReady();
    }
  }

  private handleAuthCallbackMessage(event: MessageEvent): void {
    if (!event.data || typeof event.data !== 'object') return;
    if (!('status' in event.data) || !('id' in event.data)) return;

    this.openIframeConsumerAuthentication = false;
    this.tokenizationFlow.setFlowStep(TokenizationFlowStep.AUTHENTICATING);

    const { status } = event.data;
    this.tokenization.updateAuthStatusData({ status });
  }


  /*
    El template ignora la promesa de (formSubmitted): cualquier excepción que no venga
    del API (DOM, datos) quedaría como "Uncaught (in promise)" sin llegar al log.
  */
  async tokenizationProcess(cardData: any): Promise<void> {
    try {
      await this.runTokenizationProcess(cardData);
    } catch (error) {
      this.reportUnexpectedError('tokenizationProcess', error);
    }
  }

  private async runTokenizationProcess(cardData: any): Promise<void> {
    this.loading = true;

    // Marcaje Amplitude
    this.analytics.inicioEnrolamientoTarjeta(this.tokenization.medioPagoId);

    /* PRE-AUTHENTICATION */
    const preAuthRes: PreAuthenticationModel|null = await this.preAuthenticate(cardData);
    if (preAuthRes === null) return;

    this.dataCollectionOrigin = new URL(preAuthRes.dataCollectorUrl).origin;
    this.submitIframeDeviceDataCollectionForm(preAuthRes);
    try {
      await this.waitForDataCollectionResponse(12000);
    } catch (error) {
      // Cardinal no respondió el Device Data Collection a tiempo (p. ej. iframe bloqueado por CSP)
      this.errorHandler.reportError(error, ErrorType.DATA_COLLECTION_TIMEOUT);
      return;
    }

    /* AUTHENTICATION */
    const authRes: AuthenticationModel|null = await this.authenticate();  
    if (authRes === null) return;

    if (authRes.statusCode == "S01") {
      // Tarjeta autenticada. Pasa directo a enrolamiento con status "AUTENTICADA".
      // loading se mantiene hasta ENROLLING: durante el post-auth el formulario sigue
      // en pantalla y, sin el overlay, se podría volver a enviar.
      this.tokenization.updateAuthStatusData({ status: AuthStatus.AUTENTICADA });
    } else if (authRes.statusCode == "S00") {
      // Abre iframe para autenticación con emisor
      this.submitIframeConsumerAuthenticationForm(authRes);
      this.loading = false;
    } else {
      this.errorHandler.reportError(authRes, ErrorType.AUTH);
      this.loading = false;
    }

    /* TOKENIZATION */
    this.tokenization.authStatus$
      .pipe(takeUntil(this.unsubscriber))
      .subscribe(async (data: {status: string}) => {
        try {
          await this.handleAuthStatus(data);
        } catch (error) {
          this.reportUnexpectedError('handleAuthStatus', error);
        }
      });
  }

  private async handleAuthStatus(data: {status: string}): Promise<void> {
    if (data.status === '0') {
      this.errorHandler.reportError(null, ErrorType.STATUS_0);
      return;
    }

    /* POST-AUTHENTICATION */
    const postRes = await this.postAuthenticate();

    if (postRes === null) return;
    if (postRes.statusCode !== "S00") {
      this.errorHandler.reportError(postRes, ErrorType.POST_AUTH);
      return;
    }

    /* TOKENIZATION */
    this.loading = false;
    this.tokenizationFlow.setFlowStep(TokenizationFlowStep.ENROLLING);
    const enrollRes = await this.enroll();
    if (enrollRes === null) return;

    this.tokenization.setSuccessMessageFromEnrollResponse(enrollRes);
    this.tokenizationFlow.setFlowStep(TokenizationFlowStep.SUCCESS);
  }

  private reportUnexpectedError(context: string, error: unknown): void {
    this.loading = false;
    // Es un error de JS, no del API: ErrorModel no se construye desde un Error y su
    // stack no debe viajar al log, así que se reporta con el payload genérico.
    console.error(context, Globals.sanitizeForLog(error));
    this.errorHandler.reportError(null, ErrorType.UNKNOWN);
  }

  private async waitForDataCollectionResponse(timeoutMs: number) {
    await firstValueFrom(
      this.tokenization.dataCollectionReady$
        .pipe(
          filter((isReady) => isReady === true),
          take(1),
          timeout(timeoutMs)
        )
    );
  }

  private async preAuthenticate(cardData: any): Promise<PreAuthenticationModel|null> {
    const preAuthRes: PreAuthenticationModel|null = await this.tokenization.call3DSPreAuthentication(cardData); 
    return preAuthRes;
  }

  private async authenticate(): Promise<AuthenticationModel|null> {
    const authRes: AuthenticationModel|null = await this.tokenization.call3DSAuthentication();
    return authRes;
  }

  private async postAuthenticate(): Promise<PostAuthenticationModel|null> {
    const postRes: PostAuthenticationModel|null = await this.tokenization.call3DSPostAuthorization();
    if (postRes == null) {
      this.errorHandler.errors$.pipe(take(1)).subscribe((errorModel: ErrorModel) => {
        this.analytics.autenticacionEnrolamientoTarjeta(this.tokenization.medioPagoId, errorModel.statusCode+'');
      });
    } else {
      this.analytics.autenticacionEnrolamientoTarjeta(this.tokenization.medioPagoId, postRes.statusCode || '');
    }
    return postRes;
  }

  private async enroll(): Promise<EnrollModel|null> {
    const enrollRes: EnrollModel|null = await this.tokenization.callEnrollCard();
    if (enrollRes == null) {
      this.errorHandler.errors$.pipe(take(1)).subscribe((errorModel: ErrorModel) => {
        this.analytics.finEnrolamientoTarjeta(this.tokenization.medioPagoId, errorModel.statusCode+'');
      });
    } else {
      this.analytics.finEnrolamientoTarjeta(this.tokenization.medioPagoId, enrollRes.statusCode || '');
    }
    return enrollRes;
  }

  ngOnDestroy() {
    this.unsubscriber.next();
    this.unsubscriber.complete();
  }
  

  private submitIframeDeviceDataCollectionForm(preAuthRes: PreAuthenticationModel): void {
    const form = document.getElementById('cardinal_collection_form') as HTMLFormElement;
    const input = document.getElementById('cardinal_collection_form_input') as HTMLInputElement;
    
    form.action = preAuthRes.dataCollectorUrl;
    input.value = preAuthRes.dataCollectorKey;
  
    form.submit();
  }

  private submitIframeConsumerAuthenticationForm(authRes: AuthenticationModel): void {
    const form = document.getElementById('step_up_form') as HTMLFormElement;
    const input = document.getElementById('step_up_form_jwt_input') as HTMLInputElement;

    form.action = authRes.issuerUrl;
    input.value = authRes.issuerKey;
  
    this.openIframeConsumerAuthentication = true;
    // Sin un pequeño delay, el iframe se abre en un tab nuevo, porque
    // el navegador no alcanza a reconocer que el iframe es parte de la página,
    // (dado que no se renderiza inicialmente en el DOM) y como el height es vh-100, 
    // no hay otro lugar donde ponerlo que en un tab a parte
    setTimeout(() => {
      form.submit();
    }, 100);
  }

}

